import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { usePets } from '@/shared/hooks/queries/use-pets'
import { usePolicies } from '@/shared/hooks/queries/use-policies'
import { useBackGesture } from '@/shared/hooks/use-back-gesture'
import { sendEvent } from '@/shared/hooks/use-analytics'
import { formatDate } from '@/shared/helpers/format-date'
import { ROUTES } from '@/shared/constants/routes'
import { ANIMATION_DURATION_MS } from '@/shared/constants/timings'
import { PET_WEIGHT, YES_NO_UNKNOWN } from '@/shared/types/global.types'
import type { PetWeight, YesNoUnknown } from '@/shared/types/global.types'
import { postMessageToParent } from '@/shared/helpers/post-message'
import { petDefaultIdCardImage } from '@/shared/assets'
import { ONBOARDING_MESSAGE } from '@/shared/constants/onboarding'
import { useFeatureFlagVariantKey } from 'posthog-js/react'
import { FEATURE_FLAGS } from '@/shared/constants/feature-flags'

const HIDE_PRIMARY_VET_ONBOARDING_TEST_VARIANT = 'test'

const FORM_STEP_COUNT = 2

const FORM_STEP_EVENT_NAMES = ['ob_pet_form_1', 'ob_pet_form_2'] as const

export const WEIGHT_OPTIONS = [
  { value: PET_WEIGHT.UNDER_2KG, key: 'under2' },
  { value: PET_WEIGHT.FROM_2_TO_5KG, key: '2to5' },
  { value: PET_WEIGHT.FROM_5_TO_10KG, key: '5to10' },
  { value: PET_WEIGHT.FROM_10_TO_20KG, key: '10to20' },
  { value: PET_WEIGHT.OVER_20KG, key: 'over20' },
]

export type PetFormValues = {
  birthDate: Date | undefined
  weight: PetWeight | ''
  petPictureBase64: string | undefined
  lastVaccinationDate: Date | undefined
  hasMicrochip: YesNoUnknown
  isNeutered: YesNoUnknown
  primaryVetId: string
  primaryVetName: string
}

function toApiValues(values: PetFormValues) {
  return {
    birthDate: values.birthDate,
    ...(values.weight !== '' && { weight: values.weight }),
    lastVaccinationDate: values.lastVaccinationDate,
    hasMicrochip: values.hasMicrochip,
    isNeutered: values.isNeutered,
    ...(values.primaryVetId && { primaryVetId: values.primaryVetId }),
    ...(values.petPictureBase64 && {
      petPicture: {
        fileInBase64: values.petPictureBase64.split(',')[1] ?? '',
        fileExtension:
          values.petPictureBase64.split(';')[0]?.split('/')[1] ?? 'jpg',
      },
    }),
  }
}

export function useOnboardingPetForm() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [isFlipping, setIsFlipping] = useState(false)
  const pendingStepRef = useRef<number | undefined>(undefined)
  const hasResetRef = useRef(false)

  const { data: pets, isLoading: isPetsLoading, updateMutation } = usePets()
  const { data: policies, isLoading: isPoliciesLoading } = usePolicies()

  const onboardingPolicy = policies?.[0]
  const onboardingPet =
    onboardingPolicy && pets?.find((pet) => pet.id === onboardingPolicy?.petId)
  const hideVetPrimaryVariant = useFeatureFlagVariantKey(
    FEATURE_FLAGS.HIDE_PRIMARY_VET_ONBOARDING,
  )
  const hideVetPrimaryField =
    hideVetPrimaryVariant === HIDE_PRIMARY_VET_ONBOARDING_TEST_VARIANT &&
    onboardingPolicy?.isFreeLook

  const formMethods = useForm<PetFormValues>({
    defaultValues: {
      birthDate: undefined,
      weight: '',
      petPictureBase64: undefined,
      lastVaccinationDate: undefined,
      hasMicrochip: YES_NO_UNKNOWN.I_DONT_KNOW,
      isNeutered: YES_NO_UNKNOWN.I_DONT_KNOW,
      primaryVetId: '',
      primaryVetName: '',
    },
    mode: 'all',
  })

  const {
    handleSubmit: formHandleSubmit,
    getValues,
    watch,
    reset,
    formState: { isDirty },
  } = formMethods

  const watchedBirthDate = watch('birthDate')
  const watchedWeight = watch('weight')
  const watchedPicture = watch('petPictureBase64')
  const watchedVetName = watch('primaryVetName')

  useEffect(() => {
    if (!onboardingPet || hasResetRef.current) return
    hasResetRef.current = true
    reset({
      birthDate: onboardingPet.birthDate
        ? new Date(onboardingPet.birthDate)
        : undefined,
      weight: onboardingPet.weight ?? '',
      lastVaccinationDate: onboardingPet.lastVaccinationDate
        ? new Date(onboardingPet.lastVaccinationDate)
        : undefined,
      hasMicrochip: onboardingPet.hasMicrochip ?? YES_NO_UNKNOWN.I_DONT_KNOW,
      isNeutered: onboardingPet.isNeutered ?? YES_NO_UNKNOWN.I_DONT_KNOW,
      primaryVetId: onboardingPet.primaryVetId ?? '',
      primaryVetName: onboardingPet.primaryVetName ?? '',
    })
  }, [onboardingPet, reset])

  useEffect(() => {
    sendEvent('ob_pet_form_started')
    sendEvent('ob_pet_form_1')
  }, [])

  const flipToStep = useCallback((nextStep: number) => {
    pendingStepRef.current = nextStep
    setIsFlipping(true)
    setTimeout(() => {
      document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' })
    }, ANIMATION_DURATION_MS)
  }, [])

  useEffect(() => {
    if (!isFlipping) return
    const timer = setTimeout(() => {
      if (pendingStepRef.current !== undefined) {
        setCurrentStep(pendingStepRef.current)
        pendingStepRef.current = undefined
      }
      setIsFlipping(false)
    }, ANIMATION_DURATION_MS)
    return () => clearTimeout(timer)
  }, [isFlipping])

  const handleNext = useCallback(() => {
    if (currentStep < FORM_STEP_COUNT - 1) {
      const nextStep = currentStep + 1
      sendEvent('ob_pet_form_1_completed')
      const event = FORM_STEP_EVENT_NAMES[nextStep]
      if (event) {
        sendEvent(event)
      }
      flipToStep(nextStep)
      return
    }
    formHandleSubmit((values) => {
      if (!onboardingPet?.id) return
      if (!isDirty) {
        postMessageToParent(ONBOARDING_MESSAGE.COMPLETE_ONBOARDING)
        sendEvent('ob_pet_form_2_completed')
        sendEvent('ob_pet_form_completed')
        navigate({ to: ROUTES.HOME })
        return
      }
      updateMutation.mutate(
        { id: onboardingPet.id, body: toApiValues(values) },
        {
          onSuccess: () => {
            postMessageToParent(ONBOARDING_MESSAGE.COMPLETE_ONBOARDING)
            sendEvent('ob_pet_form_2_completed')
            sendEvent('ob_pet_form_completed')
            navigate({ to: ROUTES.HOME })
          },
        },
      )
    })()
  }, [
    currentStep,
    flipToStep,
    formHandleSubmit,
    onboardingPet,
    updateMutation,
    navigate,
    isDirty,
  ])

  const handleBack = useCallback(() => {
    if (currentStep > 0) flipToStep(currentStep - 1)
  }, [currentStep, flipToStep])

  const handleSkip = useCallback(() => {
    if (onboardingPet?.id && isDirty) {
      updateMutation.mutate({
        id: onboardingPet.id,
        body: toApiValues(getValues()),
      })
    }
    postMessageToParent(ONBOARDING_MESSAGE.COMPLETE_ONBOARDING)
    sendEvent('ob_pet_form_skipped')
    navigate({ to: ROUTES.HOME })
  }, [navigate, onboardingPet, updateMutation, getValues, isDirty])

  useBackGesture(handleBack, currentStep > 0)

  const petPicture = watchedPicture ?? onboardingPet?.petPictureUrl
  const cardImageUrl = petPicture ? petPicture : petDefaultIdCardImage
  const cardBirthDate = watchedBirthDate
    ? formatDate(watchedBirthDate)
    : undefined
  const cardWeightKey =
    watchedWeight !== ''
      ? WEIGHT_OPTIONS.find((o) => o.value === watchedWeight)?.key
      : undefined

  return {
    currentStep,
    isFlipping,
    formMethods,
    isDataLoading: isPetsLoading || isPoliciesLoading,
    isSubmitting: updateMutation.isPending,
    cardImageUrl,
    hidePhotoBackground: !petPicture,
    cardBirthDate,
    cardWeightKey,
    cardVetName: watchedVetName,
    handleNext,
    handleBack,
    handleSkip,
    onboardingPet,
    onboardingPolicy,
    hideVetPrimaryField,
  }
}
